import { Link } from '@tanstack/react-router';
import { WarningIcon } from '@phosphor-icons/react';
import { T, Var } from 'gt-react';
import type { AgentStartHeldResource, AgentStartReadiness } from '@erp/rpc';
import { LeaseHolderLine } from 'src/components/agent-resources/LeaseHolderNotice';
import { anyLeaseHeldByFinishedRun } from 'src/components/agent-resources/leaseHolders';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import paths from 'src/paths';

function HeldResourceList({
  resources,
}: {
  resources: readonly AgentStartHeldResource[];
}) {
  return (
    <ul className="mt-2 space-y-2">
      {resources.map((resource) => (
        <li key={resource.resourceId} className="space-y-1">
          <div className="text-xs font-medium">
            <Var>{resource.resourceName}</Var>
          </div>
          {resource.holders.map((holder) => (
            <LeaseHolderLine key={holder.leaseId} holder={holder} />
          ))}
        </li>
      ))}
    </ul>
  );
}

/**
 * A required resource whose slot belongs to a run that already finished. It
 * will not free itself before the lease TTL (hours, for a desktop), and every
 * run this agent attempts meanwhile fails without starting. Shown even while
 * a run is live: that run is not this agent's.
 */
function HeldByFinishedRunAlert({
  resources,
}: {
  resources: readonly AgentStartHeldResource[];
}) {
  return (
    <AlertContainer className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
      <WarningIcon />
      <AlertTitle>
        <T context="agent page warning title when a required resource is leased to a run that already finished">
          Required resource is held by a finished run
        </T>
      </AlertTitle>
      <AlertDescription className="text-amber-800 dark:text-amber-300">
        <p>
          <T context="agent page warning when a required resource is leased to a run that already finished, so this agent cannot start">
            A resource this agent needs is still leased to a run that already
            finished, so the agent cannot start and its runs fail. The lease
            frees itself only when it expires. Release it now to let the agent
            start, or wait for it to expire.
          </T>
        </p>
        <HeldResourceList resources={resources} />
        <Link
          to="/settings/organization/$section"
          params={{ section: 'agent-resources' }}
          className="mt-2 inline-block font-medium underline"
        >
          <T context="agent page link to Settings › Agent resources where leases are listed and released">
            Open Agent resources
          </T>
        </Link>
      </AlertDescription>
    </AlertContainer>
  );
}

/**
 * Explains why a task-managed agent is not starting on its own. A blocking
 * misconfiguration gets a warning banner that links to the fix; a temporary
 * wait gets one quiet line (hidden while a run is live, when "waiting" would
 * be wrong); anything else renders nothing.
 */
export function AgentStartReadinessNotice({
  readiness,
  agentDefinitionId,
  isRunning,
}: {
  readiness: AgentStartReadiness | undefined;
  agentDefinitionId: string;
  isRunning: boolean;
}) {
  const heldResources = readiness?.heldResources ?? [];
  if (
    readiness?.reason === 'resources_unavailable' &&
    anyLeaseHeldByFinishedRun(heldResources)
  ) {
    return <HeldByFinishedRunAlert resources={heldResources} />;
  }

  if (
    readiness?.reason === 'resources_unavailable' &&
    readiness.kind === 'blocking'
  ) {
    return (
      <AlertContainer className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
        <WarningIcon />
        <AlertTitle>
          <T context="agent page warning title when a required resource is paused">
            Required resource is paused
          </T>
        </AlertTitle>
        <AlertDescription className="text-amber-800 dark:text-amber-300">
          <p>
            <T context="agent page warning when a required resource is paused so the agent cannot start">
              A required resource is paused, so this agent cannot start. Runs
              fail instead of finishing as if the queue were empty. Resume the
              resource in Settings, then run the agent again.
            </T>
          </p>
          <Link
            to="/settings/organization/$section"
            params={{ section: 'agent-resources' }}
            className="font-medium underline"
          >
            <T context="agent page link to Settings › Agent resources where a paused resource is resumed">
              Resume the resource
            </T>
          </Link>
        </AlertDescription>
      </AlertContainer>
    );
  }

  if (readiness?.reason === 'no_start_conditions_configured') {
    return (
      <AlertContainer className="border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
        <WarningIcon />
        <AlertTitle>
          <T context="agent page warning title when a task-managed agent has no start conditions">
            No start conditions configured
          </T>
        </AlertTitle>
        <AlertDescription className="text-amber-800 dark:text-amber-300">
          <p>
            <T context="agent page warning when a task-managed agent has no start conditions">
              This agent has task management enabled but no start conditions, so
              it will never start on its own.
            </T>
          </p>
          <Link
            to={paths.authenticated.backgroundAgents.definitionTaskManagement}
            params={{ agentDefinitionId }}
            className="font-medium underline"
          >
            <T context="agent page link to the agent's Task Management settings tab">
              Add start conditions
            </T>
          </Link>
        </AlertDescription>
      </AlertContainer>
    );
  }

  if (isRunning) return null;

  if (readiness?.reason === 'resources_unavailable') {
    return (
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>
          <T context="agent page note while a task-managed agent waits for a leased resource">
            Waiting for a required resource to become available before starting.
          </T>
        </p>
        {heldResources.length > 0 && (
          <HeldResourceList resources={heldResources} />
        )}
      </div>
    );
  }

  if (readiness?.reason === 'start_conditions_not_met') {
    return (
      <p className="text-sm text-muted-foreground">
        <T context="agent page note while a task-managed agent waits for its start conditions">
          Waiting for its start conditions to be met before starting.
        </T>
      </p>
    );
  }

  return null;
}
